// @flow
import { getAllElementsOfArrayExceptLast, getLastElementOfArray } from '@src/common-utils/utils';

import {
  convertDimArrayToDimsKey,
  getConnectorTierGivenConnectorNameAndTierMapping,
  getDiscountMinMaxRangeBasedOnGuidanceRangesAndUsageValue,
  getGuidanceForMetric,
  getIfGivenConnectorFallsIntoAnyTierProvidedByGuidanceObject,
  getIfGivenDimsArrayIsSubSetOfAnyKeyInTheGivenSetOfKeys,
  getMinMaxDiscountRangeForGivenMLDSingleSelectRow,
  getUsagesForMetric,
  isFlatDimsValidBasedOnUsage,
} from './utils';
import { getMultiSelectUsageForDimensionCombination } from './usage-utils';

// ═══════════════════════════════════════════════════════════════════════════════
// Dropdown Option Guidance Validators
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Validates if a dimension value should be enabled in dropdown based on guidance for non-connector metrics.
 *
 * This function is used for both dropdown option validation and row validation for all non-connector metrics
 *
 * This function is used during dropdown option rendering to determine if an option should be
 * selectable by non-admin users. It checks if the partially filled dimensions array is a subset
 * of any guidance key.
 *
 * Business Logic:
 * - Non-admins can only select dimension values that exist in guidance
 * - Guidance keys may contain "all" which matches any value from the user
 * - Users cannot enter "all" themselves (handled separately by disabling ALL option)
 *
 * @example
 * // User selecting provider for KafkaNumCKUs metric
 * isFlatDimsValidBasedOnGuidanceForNonConnectors(mldGuidance, ['aws'], 'KafkaNumCKUs')
 * // Returns: true (aws is subset of guidance keys)
 *
 * @example
 * // User selecting a provider not in any guidance key
 * isFlatDimsValidBasedOnGuidanceForNonConnectors(mldGuidance, ['azure'], 'KafkaNumCKUs')
 * // Returns: false (azure not found)
 *
 * @param {Object} mldGuidance - The MLD guidance object from hook
 * @param {string[]} partiallyFilledDimsArray - Dimension values selected so far
 * @param {string} metricName - The metric name
 * @returns {boolean} True if option should be enabled, false if disabled
 */
export const isFlatDimsValidBasedOnGuidanceForNonConnectors = (
  mldGuidance,
  partiallyFilledDimsArray,
  metricName
) => {
  const guidance = getGuidanceForMetric(metricName, mldGuidance);
  if (!guidance) {
    return false;
  }
  return getIfGivenDimsArrayIsSubSetOfAnyKeyInTheGivenSetOfKeys(
    partiallyFilledDimsArray,
    Object.keys(guidance)
  );
};

/**
 * Validates if a dimension value should be enabled in dropdown based on guidance for connector metrics.
 *
 * Connector metrics have special validation:
 * - First column (provider): Always enabled - guidance applies to all providers
 * - Second column (smt_type): Always enabled - guidance applies to all smt_types
 * - Third column (connector_sku): Enabled only if connector exists in tier mapping
 *
 * Column determined by array length: 1 = provider, 2 = smt_type, 3 = connector_sku
 *
 * @example
 * // Validating provider (first column)
 * isDimsValidForDropdownOptionBasedOnGuidanceForConnectors(mldGuidance, ['aws'], 'ConnectNumTasks')
 * // Returns: true (provider always enabled)
 *
 * @example
 * // Validating smt_type (second column)
 * isDimsValidForDropdownOptionBasedOnGuidanceForConnectors(mldGuidance, ['aws', 'default'], 'ConnectNumTasks')
 * // Returns: true (smt_type always enabled)
 *
 * @example
 * // Validating connector in tier mapping
 * isDimsValidForDropdownOptionBasedOnGuidanceForConnectors(mldGuidance, ['aws', 'S3SinkConnector'], 'ConnectNumTasks')
 * // Returns: true/false based on tier mapping
 *
 * @param {Object} mldGuidance - The MLD guidance object
 * @param {string[]} partiallyFilledDimsArray - Dimension values (length determines column)
 * @param {string} _metricName - Unused, kept for consistent signature
 * @returns {boolean} True if option should be enabled
 */
export const isDimsValidForDropdownOptionBasedOnGuidanceForConnectors = (
  mldGuidance,
  partiallyFilledDimsArray,
  _metricName
) => {
  // ConnectNumTasks has 3 columns: provider, smt_type, connector_sku
  // so, if the length is less than 3, then, it is the provider or smt_type column and it is always enabled
  // if the length is 3, then, it is the connector_sku column and it is enabled only if it exists in any tier from guidance
  // If in future, the number of dimension columns changes, we need to change this accordingly
  if (partiallyFilledDimsArray.length < 3) {
    return true;
  }
  // If the partially filled dimensions array has more than one value,
  // then, it is the connector and it is enabled only if it falls into any of the tiers that are present in the guidance
  // Here, we are assuming the last column is the connector_sku,
  // if in future, we change the column configuration, we need to change this accordingly
  return getIfGivenConnectorFallsIntoAnyTierProvidedByGuidanceObject(
    partiallyFilledDimsArray.at(-1),
    mldGuidance.connectTierMappingMap
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Row Dimension Validators (Config-based)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Validates if an entire row's dimension values are valid for non-connector metrics.
 *
 * For non-connector metrics (single-select), the dimension values are always an array of strings
 * (e.g., ['aws', 'standard', 'mz', 'internet']). This validator checks both usage and guidance.
 *
 * @example
 * // Validating a complete row for KafkaNumCKUs
 * isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors(mldGuidance, ['aws', 'standard', 'mz', 'internet'], 'KafkaNumCKUs')
 * // Returns: true if the combination exists in both usage and guidance
 *
 * @param {Object} mldGuidance - The MLD guidance object
 * @param {string[]} dimensionValuesArray - Complete row dimension values
 * @param {string} metricName - The metric name
 * @returns {boolean} True if row dimensions are valid
 */
export const isRowDimsValidBasedOnUsageAndGuidanceForNonConnectors = (
  mldGuidance,
  dimensionValuesArray,
  metricName
) => {
  const isValidAccordingToUsage = isFlatDimsValidBasedOnUsage(
    metricName,
    mldGuidance,
    dimensionValuesArray
  );

  const isValidAccordingToGuidance = isFlatDimsValidBasedOnGuidanceForNonConnectors(
    mldGuidance,
    dimensionValuesArray,
    metricName
  );

  return isValidAccordingToUsage && isValidAccordingToGuidance;
};

const isGivenConnectorDimensionsArrayValidBasedOnUsage = (metricName, mldGuidance, dimsArray) => {
  const usages = getUsagesForMetric(metricName, mldGuidance);
  if (!usages) {
    return false;
  }

  /**
   * dimsArray would be something like ["aws" , "default", ["A", "B", "C"]] where the last column would contain the connector values
   * and the others will be non-connector values. For e.g., for 'Connect Num Task', 'provider' and 'smt type' are the first 2 values and
   * 'connector sku' would be the 3rd value in the array i.e., the dimsArray would be ['provider', 'smt type', ['connector sku value1', 'connector sku value2']];
   *
   * we need to explode it into its sub parts and check if everything exists in usage or not i.e., ["aws" , "default", ["A", "B", "C"]] will be split into
   * ["aws" , "default", "A"], ["aws" , "default", "B"], ["aws" , "default", "C"]
   *
   *
   * The below logic will work as long as connector values are the last values in the array
   */
  const connectorsArray = getLastElementOfArray(dimsArray);
  const nonConnectorsDimsArray = getAllElementsOfArrayExceptLast(dimsArray);

  const usageKeys = Object.keys(usages);

  // For each value in the connectors column array, create a combination and check if it exists
  for (const connectorValue of connectorsArray) {
    const fullCombination = [...nonConnectorsDimsArray, connectorValue];
    const dimKey = convertDimArrayToDimsKey(fullCombination);

    // Check if this key exists directly in usage keys
    if (!usageKeys.includes(dimKey)) {
      return false;
    }
  }

  // All combinations exist
  return true;
};

const isGivenConnectorDimsArrayValidBasedOnGuidance = (
  connectTierMappingMap,
  dimensionValuesArray
) => {
  // Connectors are assumed to always be the last element in the dimensions array.
  // If that changes, the extraction logic here needs to be updated.
  const connectors = getLastElementOfArray(dimensionValuesArray);
  for (const connectorName of connectors) {
    if (
      !getIfGivenConnectorFallsIntoAnyTierProvidedByGuidanceObject(
        connectorName,
        connectTierMappingMap
      )
    ) {
      return false;
    }
  }

  return true;
};

/**
 * Validates if an entire row's dimension values are valid for connector metrics.
 *
 * @param {Object} mldGuidance - The MLD guidance object
 * @param {Array} dimensionValuesArray - [...nonConnectorDims, connectors[]] structure
 * @param {string} metricName - The metric name
 * @returns {boolean} True if row dimensions are valid
 */
export const isRowDimsValidBasedOnUsageAndGuidanceForConnectors = (
  mldGuidance,
  dimensionValuesArray,
  metricName
) => {
  const isValidAccordingToUsage = isGivenConnectorDimensionsArrayValidBasedOnUsage(
    metricName,
    mldGuidance,
    dimensionValuesArray
  );

  const { connectTierMappingMap } = mldGuidance;
  const isValidAccordingToGuidance = isGivenConnectorDimsArrayValidBasedOnGuidance(
    connectTierMappingMap,
    dimensionValuesArray
  );

  return isValidAccordingToUsage && isValidAccordingToGuidance;
};

// ═══════════════════════════════════════════════════════════════════════════════
// Discount Range Validators
// ═══════════════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────────────────
// Get discount data for MULTI-SELECT metrics (Connectors)
// ─────────────────────────────────────────────────────────────────────────────
// Flow: For each connector → get tier → get usage → get discountRange based on the usage for that connector
// We need to get the ranges for all of them and then finally take the min/max based on intersection logic
// Returns BOTH intersected range and per-connector limits in one call to avoid duplicate iterations
// TODO::MLD Will Handle All Case in Future
const getPerConnectorDiscountRanges = (selectedDims, mldGuidance, metricName) => {
  const { connectTierMappingMap } = mldGuidance;
  const guidanceData = getGuidanceForMetric(metricName, mldGuidance);

  const allSelectedConnectorNames = getLastElementOfArray(selectedDims);

  // Get usage for each selected connector
  const usagePerConnector = getMultiSelectUsageForDimensionCombination(
    mldGuidance,
    selectedDims,
    metricName
  );

  // Get discount range for each selected value
  const perConnectorLimits = [];
  for (const connector of allSelectedConnectorNames) {
    // For multi-select, guidance key comes from tier mapping (e.g., connector → tier number)
    const tierNumber = getConnectorTierGivenConnectorNameAndTierMapping(
      connector,
      connectTierMappingMap
    );
    const usage = usagePerConnector[connector];
    const guidanceRanges = guidanceData?.[tierNumber];
    const range = getDiscountMinMaxRangeBasedOnGuidanceRangesAndUsageValue(usage, guidanceRanges);

    perConnectorLimits.push({
      connector,
      minDiscount: range.minDiscount,
      maxDiscount: range.maxDiscount,
    });
  }

  return perConnectorLimits;
};

// ─────────────────────────────────────────────────────────────────────────────
// Intersection of all ranges into final min/max (intersection of all ranges)
// ─────────────────────────────────────────────────────────────────────────────
// Input:  [{ minDiscount: 5, maxDiscount: 20 }, { minDiscount: 10, maxDiscount: 25 }]
// Output: { minDiscount: 10, maxDiscount: 20 }
const getMinAndMaxRangeBasedOnIntersectionOfDiscountRanges = (ranges) => {
  let minDiscount = -Infinity;
  let maxDiscount = Infinity;

  for (const range of ranges) {
    minDiscount = Math.max(minDiscount, range.minDiscount);
    maxDiscount = Math.min(maxDiscount, range.maxDiscount);
  }

  return {
    minDiscount,
    maxDiscount,
  };
};

/**
 * Gets discount range for connector metrics with per-connector limits.
 * Returns { minDiscount, maxDiscount, connectorToDiscountListString } for detailed error messages.
 *
 * @param {Array} dimensionValues - Selected dimension values including connector names array
 * @param {Object} mldGuidance - MLD guidance object
 * @param {string} metricName - The metric name
 * @returns {{ minDiscount: number, maxDiscount: number, connectorToDiscountListString: string }}
 */
export const getDiscountRangeForConnectorMetrics = (dimensionValues, mldGuidance, metricName) => {
  const perConnectorLimits = getPerConnectorDiscountRanges(
    dimensionValues,
    mldGuidance,
    metricName
  );

  const { minDiscount, maxDiscount } =
    getMinAndMaxRangeBasedOnIntersectionOfDiscountRanges(perConnectorLimits);

  const connectorToDiscountListString = perConnectorLimits
    .map(
      ({ connector, maxDiscount: connectorMaxDiscount }) =>
        `• ${connector}: max ${connectorMaxDiscount}%`
    )
    .join('\n');

  return {
    minDiscount,
    maxDiscount,
    connectorToDiscountListString,
  };
};

/**
 * Gets discount range for single-select metrics with unified interface.
 * Returns { minDiscount, maxDiscount, connectorToDiscountListString: null } for consistency
 * with connector metrics which include connectorToDiscountListString.
 *
 * @param {string[]} dimensionValues - Selected dimension values
 * @param {Object} mldGuidance - MLD guidance object
 * @param {string} metricName - The metric name
 * @returns {{ minDiscount: number, maxDiscount: number, connectorToDiscountListString: null } | null}
 */
export const getDiscountRangeForSingleSelectMetrics = (
  dimensionValues,
  mldGuidance,
  metricName
) => {
  const range = getMinMaxDiscountRangeForGivenMLDSingleSelectRow(
    dimensionValues,
    metricName,
    mldGuidance
  );
  if (!range) {
    return null;
  }
  return {
    minDiscount: range.minDiscount,
    maxDiscount: range.maxDiscount,
    connectorToDiscountListString: null,
  };
};
